import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { fetchCustomers, fetchProducts } from '@/api/commercial';
import { createRfq, type CreateRfqPayload } from '@/api/rfqs';
import { TRADING_COUNTRIES } from '@/features/rfqs/countries';
import { RfqPastePanel } from '@/features/rfqs/RfqPastePanel';
import { chargeableWeight } from '@/features/rfqs/schema';

const STEPS = ['Route', 'Cargo', 'Lines & dates'] as const;

/**
 * THE FULL ENUMS, not a shortlist.
 *
 * These lists were the six and seven most common values, which was fine while every RFQ was
 * typed — and became wrong the moment a request could be read from a document. The parser
 * classifies against the whole vocabulary the server accepts, so a customs-brokerage or
 * bulk-liquid enquiry would have been read correctly and then dropped into a Select with no
 * matching option: a silently blank field, on the one job whose type was least ambiguous.
 *
 * Kept in the server's evaluation order rather than alphabetically, so the list reads the
 * way the trade talks.
 */
const SERVICES = [
  { value: 'road_ftl', label: 'Road — full truckload' },
  { value: 'road_ltl', label: 'Road — part load' },
  { value: 'rail', label: 'Rail' },
  { value: 'sea_fcl', label: 'Sea — full container' },
  { value: 'sea_lcl', label: 'Sea — groupage' },
  { value: 'air', label: 'Air' },
  { value: 'warehousing', label: 'Warehousing' },
  { value: 'customs_brokerage', label: 'Customs brokerage' },
  { value: 'last_mile', label: 'Last mile' },
  { value: 'value_added', label: 'Value added' },
];

const CARGO_TYPES = [
  { value: 'general_cargo', label: 'General cargo' },
  { value: 'palletised', label: 'Palletised' },
  { value: 'containerised', label: 'Containerised' },
  { value: 'bulk_dry', label: 'Bulk (dry)' },
  { value: 'bulk_liquid', label: 'Bulk (liquid)' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'hazardous', label: 'Hazardous' },
  { value: 'oversized', label: 'Oversized' },
  { value: 'live_animals', label: 'Live animals' },
  { value: 'vehicles', label: 'Vehicles' },
];

const UNITS = [
  { value: 'shipment', label: 'Per shipment' },
  { value: 'container', label: 'Per container' },
  { value: 'tonne', label: 'Per tonne' },
  { value: 'kilogram', label: 'Per kg' },
  { value: 'day', label: 'Per day' },
];

interface LineDraft {
  description: string;
  quantity: string;
  unit_of_measure: string;
  product_id: string;
}

/**
 * The RFQ wizard.
 *
 * THREE STEPS BECAUSE THE FORM HAS THREE DISTINCT DECISIONS, not to break up a long
 * page: who and where, what the cargo is, and what is being asked for by when. Each
 * step validates before advancing, so a mistake surfaces next to the field that
 * caused it rather than in a list at the end.
 *
 * The chargeable-weight readout on step 2 is the reason the wizard exists rather
 * than a single form. 2 m³ of light cargo is charged as 666 kg by road, and a
 * salesperson has to see that BEFORE quoting — not when the customer queries the
 * invoice. The server computes the authoritative figure; this mirrors it live.
 */
export function RfqWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [values, setValues] = useState({
    customer_id: '',
    service_category: 'road_ftl',
    origin_description: '',
    origin_city: '',
    // COUNTRY IS WHAT A CORRIDOR RATE MATCHES ON. Absent from this object until now, and
    // `applyExtraction` only merges keys that already exist here — so the parser derived both
    // countries from its place gazetteer and this form threw them away, while any pricing rule
    // scoped to a country pair matched nothing at all.
    origin_country: '',
    destination_description: '',
    destination_city: '',
    destination_country: '',
    distance_km: '',
    cargo_type: 'general_cargo',
    incoterm: '',
    weight_kg: '',
    volume_m3: '',
    piece_count: '',
    currency: 'KES',
    declared_value: '',
    temperature_controlled: false,
    hazardous: false,
    oversized: false,
    high_value: false,
    un_number: '',
    ready_date: '',
    required_delivery_date: '',
    respond_by: '',
    notes: '',
  });

  const [lines, setLines] = useState<LineDraft[]>([
    { description: '', quantity: '1', unit_of_measure: 'shipment', product_id: '' },
  ]);

  const customers = useQuery({
    queryKey: ['customers', { forRfq: true }],
    queryFn: () => fetchCustomers({ status: 'active', per_page: 100 }),
  });

  const products = useQuery({
    queryKey: ['products', { activeOnly: true }],
    queryFn: () => fetchProducts({ activeOnly: true, per_page: 100 }),
  });

  const weight = Number(values.weight_kg) || 0;
  const volume = values.volume_m3 === '' ? undefined : Number(values.volume_m3);

  const chargeable = useMemo(
    () => chargeableWeight(weight, volume, values.service_category, values.cargo_type),
    [weight, volume, values.service_category, values.cargo_type],
  );

  /**
   * Merge what the parser read into the form.
   *
   * COERCED TO THE FORM'S OWN TYPES, because every input here is a controlled string and
   * handing a Number to one turns it into an uncontrolled input for the rest of its life —
   * which shows up much later as a field that silently refuses to be edited.
   *
   * Only the keys the parser actually filled are touched. Anything it could not read keeps
   * whatever is already in the form, so pasting a second document adds to the first rather
   * than blanking it.
   */
  const applyExtraction = (parsed: Record<string, string | number | boolean>) => {
    setValues((current) => {
      const merged = { ...current };

      for (const [field, parsedValue] of Object.entries(parsed)) {
        if (!(field in merged)) continue;

        const existing = merged[field as keyof typeof merged];

        (merged as Record<string, unknown>)[field] =
          typeof existing === 'boolean' ? Boolean(parsedValue) : String(parsedValue);
      }

      return merged;
    });

    // Clear stale validation: a field the parser has just filled must not still be showing
    // "Weight is what freight is charged on".
    setErrors({});
    setStep(0);
  };

  const set = (field: keyof typeof values) => (value: unknown) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  };

  const mutation = useMutation({
    mutationFn: createRfq,
    onSuccess: (rfq) => navigate(`/rfqs/${rfq.id}`),
    onError: (error) => {
      if (error instanceof ApiError) {
        // Server-side field errors are authoritative — they include rules the
        // client cannot check, like whether the customer is on credit hold.
        setErrors(
          Object.fromEntries(
            Object.entries(error.errors ?? {}).map(([field, messages]) => [
              field.replace(/^line_items\.\d+\./, ''),
              messages[0] ?? 'Invalid',
            ]),
          ),
        );
        setSubmitError(error.detail ?? error.title);
        return;
      }

      setSubmitError('Could not create the request.');
    },
  });

  /** Per-step validation, so a mistake surfaces beside the field that caused it. */
  const validateStep = (): boolean => {
    const found: Record<string, string> = {};

    if (step === 0) {
      if (!values.customer_id) found.customer_id = 'Choose a customer.';
      if (!values.origin_description) found.origin_description = 'Where does it ship from?';
      if (!values.destination_description) found.destination_description = 'Where does it ship to?';
    }

    if (step === 1) {
      if (!(Number(values.weight_kg) > 0))
        found.weight_kg = 'Weight is what freight is charged on.';
      if (values.hazardous && !values.un_number.trim()) {
        found.un_number = 'Hazardous cargo needs its UN number.';
      }
      if (!values.hazardous && values.un_number.trim()) {
        found.un_number = 'A UN number only applies to hazardous cargo.';
      }
    }

    if (step === 2) {
      if (lines.every((line) => !line.description.trim())) {
        found.line_items = 'Add at least one line — there would be nothing to price.';
      }
      if (
        values.ready_date &&
        values.required_delivery_date &&
        values.required_delivery_date < values.ready_date
      ) {
        found.required_delivery_date = 'Delivery cannot be required before the cargo is ready.';
      }
    }

    setErrors(found);

    return Object.keys(found).length === 0;
  };

  const submit = () => {
    if (!validateStep()) return;

    const payload: CreateRfqPayload = {
      customer_id: values.customer_id,
      service_category: values.service_category,
      cargo_type: values.cargo_type,
      origin_description: values.origin_description,
      destination_description: values.destination_description,
      weight_kg: Number(values.weight_kg),
      currency: values.currency,
      temperature_controlled: values.temperature_controlled,
      hazardous: values.hazardous,
      oversized: values.oversized,
      high_value: values.high_value,
      line_items: lines
        .filter((line) => line.description.trim())
        .map((line) => ({
          description: line.description,
          quantity: Number(line.quantity) || 1,
          unit_of_measure: line.unit_of_measure,
          ...(line.product_id ? { product_id: line.product_id } : {}),
        })),
      ...(values.origin_city ? { origin_city: values.origin_city } : {}),
      ...(values.origin_country ? { origin_country: values.origin_country } : {}),
      ...(values.destination_city ? { destination_city: values.destination_city } : {}),
      ...(values.destination_country ? { destination_country: values.destination_country } : {}),
      ...(values.incoterm ? { incoterm: values.incoterm } : {}),
      ...(values.volume_m3 ? { volume_m3: Number(values.volume_m3) } : {}),
      ...(values.piece_count ? { piece_count: Number(values.piece_count) } : {}),
      ...(values.distance_km ? { distance_km: Number(values.distance_km) } : {}),
      ...(values.un_number ? { un_number: values.un_number } : {}),
      ...(values.ready_date ? { ready_date: values.ready_date } : {}),
      ...(values.required_delivery_date
        ? { required_delivery_date: values.required_delivery_date }
        : {}),
      ...(values.respond_by ? { respond_by: values.respond_by } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
      // MAJOR units in the form, minor units on the wire — money is never a float
      // and never a decimal string.
      ...(values.declared_value
        ? { declared_value_minor: Math.round(Number(values.declared_value) * 100) }
        : {}),
    };

    mutation.mutate(payload);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5">New request for quotation</Typography>

      {/*
        Above the stepper, and collapsed by default. It is a shortcut into the form rather
        than a fourth step: a salesperson with the customer on the phone types straight into
        the wizard, and one with an email in front of them pastes it. Making it a step would
        force the first case through a screen they have nothing to put in.
      */}
      <RfqPastePanel onApply={applyExtraction} />

      <Stepper activeStep={step} sx={{ py: 1 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <Card variant="outlined">
        <CardContent>
          {step === 0 && (
            <Stack spacing={2}>
              <TextField
                select
                size="small"
                label="Customer"
                value={values.customer_id}
                onChange={(event) => set('customer_id')(event.target.value)}
                error={Boolean(errors.customer_id)}
                helperText={
                  errors.customer_id ?? 'Only customers who can take new business appear here.'
                }
                required
              >
                {(customers.data?.data ?? []).map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.code ? `${customer.code} — ${customer.name}` : customer.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Service"
                value={values.service_category}
                onChange={(event) => set('service_category')(event.target.value)}
              >
                {SERVICES.map((service) => (
                  <MenuItem key={service.value} value={service.value}>
                    {service.label}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Ships from"
                  value={values.origin_description}
                  onChange={(event) => set('origin_description')(event.target.value)}
                  error={Boolean(errors.origin_description)}
                  helperText={errors.origin_description}
                  fullWidth
                  required
                />
                <TextField
                  size="small"
                  label="Origin city"
                  value={values.origin_city}
                  onChange={(event) => set('origin_city')(event.target.value)}
                  sx={{ minWidth: 180 }}
                />
                {/*
                  Optional, and it earns its place on a cross-border corridor: rates and duties are
                  matched on the country pair, so a blank here means a corridor rate cannot apply.
                */}
                <TextField
                  select
                  size="small"
                  label="Origin country"
                  value={values.origin_country}
                  onChange={(event) => set('origin_country')(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">
                    <em>Not stated</em>
                  </MenuItem>
                  {TRADING_COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Ships to"
                  value={values.destination_description}
                  onChange={(event) => set('destination_description')(event.target.value)}
                  error={Boolean(errors.destination_description)}
                  helperText={errors.destination_description}
                  fullWidth
                  required
                />
                <TextField
                  size="small"
                  label="Destination city"
                  value={values.destination_city}
                  onChange={(event) => set('destination_city')(event.target.value)}
                  sx={{ minWidth: 180 }}
                />
                <TextField
                  select
                  size="small"
                  label="Destination country"
                  value={values.destination_country}
                  onChange={(event) => set('destination_country')(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">
                    <em>Not stated</em>
                  </MenuItem>
                  {TRADING_COUNTRIES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                size="small"
                label="Distance (km)"
                type="number"
                value={values.distance_km}
                onChange={(event) => set('distance_km')(event.target.value)}
                helperText="Leave blank to estimate from the addresses. A figure you enter is trusted over the estimate."
                sx={{ maxWidth: 320 }}
              />
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  select
                  size="small"
                  label="Cargo type"
                  value={values.cargo_type}
                  onChange={(event) => set('cargo_type')(event.target.value)}
                  fullWidth
                >
                  {CARGO_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label="Incoterm"
                  value={values.incoterm}
                  onChange={(event) => set('incoterm')(event.target.value.toUpperCase())}
                  placeholder="e.g. DAP"
                  helperText="Decides which legs are being quoted."
                  sx={{ minWidth: 200 }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Gross weight (kg)"
                  type="number"
                  value={values.weight_kg}
                  onChange={(event) => set('weight_kg')(event.target.value)}
                  error={Boolean(errors.weight_kg)}
                  helperText={errors.weight_kg}
                  required
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Volume (m³)"
                  type="number"
                  value={values.volume_m3}
                  onChange={(event) => set('volume_m3')(event.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Pieces"
                  type="number"
                  value={values.piece_count}
                  onChange={(event) => set('piece_count')(event.target.value)}
                  sx={{ minWidth: 120 }}
                />
              </Stack>

              {/* The readout that justifies the wizard: light cargo is charged on
                  volume, and that has to be visible before quoting. */}
              {weight > 0 && (
                <Alert severity={chargeable.basis === 'volumetric' ? 'warning' : 'info'}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">
                      Chargeable weight <strong>{chargeable.chargeable.toLocaleString()} kg</strong>
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        chargeable.basis === 'volumetric'
                          ? `on volume (÷ ${chargeable.divisor} kg/m³)`
                          : chargeable.basis === 'actual'
                            ? 'on gross weight'
                            : 'volume not applicable'
                      }
                    />
                  </Stack>
                  {chargeable.basis === 'volumetric' && (
                    <Typography variant="caption">
                      This cargo takes up more space than its weight suggests, so it is charged on
                      volume. Confirmed by the server on save.
                    </Typography>
                  )}
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Declared value"
                  type="number"
                  value={values.declared_value}
                  onChange={(event) => set('declared_value')(event.target.value)}
                  helperText="Used for insurance rating."
                  fullWidth
                />
                <TextField
                  select
                  size="small"
                  label="Currency"
                  value={values.currency}
                  onChange={(event) => set('currency')(event.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  {['KES', 'USD', 'EUR', 'TZS', 'UGX'].map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction="row" spacing={2} flexWrap="wrap">
                {(
                  [
                    ['temperature_controlled', 'Temperature controlled'],
                    ['hazardous', 'Hazardous'],
                    ['oversized', 'Oversized'],
                    ['high_value', 'High value'],
                  ] as const
                ).map(([field, label]) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Switch
                        size="small"
                        checked={values[field]}
                        onChange={(event) => set(field)(event.target.checked)}
                      />
                    }
                    label={label}
                  />
                ))}
              </Stack>

              {values.hazardous && (
                <TextField
                  size="small"
                  label="UN number"
                  value={values.un_number}
                  onChange={(event) => set('un_number')(event.target.value)}
                  error={Boolean(errors.un_number)}
                  helperText={errors.un_number ?? 'Hazardous cargo cannot move without one.'}
                  required
                  sx={{ maxWidth: 240 }}
                />
              )}
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Requested lines</Typography>

              {errors.line_items && <Alert severity="error">{errors.line_items}</Alert>}

              {lines.map((line, index) => (
                <Stack
                  key={index}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    size="small"
                    label="Description"
                    value={line.description}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, description: event.target.value } : item,
                        ),
                      )
                    }
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Qty"
                    type="number"
                    value={line.quantity}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, quantity: event.target.value } : item,
                        ),
                      )
                    }
                    sx={{ minWidth: 90 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Unit"
                    value={line.unit_of_measure}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, unit_of_measure: event.target.value } : item,
                        ),
                      )
                    }
                    sx={{ minWidth: 150 }}
                  >
                    {UNITS.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Catalogue"
                    value={line.product_id}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, product_id: event.target.value } : item,
                        ),
                      )
                    }
                    sx={{ minWidth: 240 }}
                    helperText="Optional"
                    // The code alone was opaque to anyone who does not already know the rate
                    // card — 'FRT-FCL' says nothing about sea freight. The list shows both, and
                    // renderValue keeps the closed field to one line so a long name cannot
                    // stretch the row.
                    SelectProps={{
                      renderValue: (value) => {
                        const product = (products.data?.data ?? []).find(
                          (item) => item.id === value,
                        );

                        if (!product) return 'Not mapped';

                        // A middle dot, not a dash: several catalogue names contain an em dash
                        // already ('Road freight — full truckload'), and joining with another
                        // gives two in one line.
                        return `${product.code} · ${product.name}`;
                      },
                      MenuProps: { sx: { maxHeight: 420 } },
                    }}
                  >
                    <MenuItem value="">Not mapped</MenuItem>
                    {(products.data?.data ?? []).map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        <Stack spacing={0}>
                          <Typography variant="body2" fontWeight={500}>
                            {product.code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.name}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                    disabled={lines.length === 1}
                    aria-label="Remove line"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}

              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setLines((current) => [
                      ...current,
                      {
                        description: '',
                        quantity: '1',
                        unit_of_measure: 'shipment',
                        product_id: '',
                      },
                    ])
                  }
                >
                  Add line
                </Button>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Cargo ready"
                  type="date"
                  value={values.ready_date}
                  onChange={(event) => set('ready_date')(event.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Required delivery"
                  type="date"
                  value={values.required_delivery_date}
                  onChange={(event) => set('required_delivery_date')(event.target.value)}
                  error={Boolean(errors.required_delivery_date)}
                  helperText={errors.required_delivery_date}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Respond by"
                  type="date"
                  value={values.respond_by}
                  onChange={(event) => set('respond_by')(event.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText="Expires automatically past this."
                  fullWidth
                />
              </Stack>

              <TextField
                size="small"
                label="Notes"
                value={values.notes}
                onChange={(event) => set('notes')(event.target.value)}
                multiline
                rows={3}
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button
          onClick={() => setStep((current) => Math.max(0, current - 1))}
          disabled={step === 0}
        >
          Back
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (validateStep()) setStep((current) => current + 1);
            }}
          >
            Continue
          </Button>
        ) : (
          <Button variant="contained" onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create request'}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
